import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ShoppingCart, User, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import CartDrawer from './CartDrawer';

const navLinks = [
  { to: '/o-nas', label: 'O NAS' },
  { to: '/kontakt', label: 'KONTAKT' },
  { to: '/faq', label: 'FAQ' },
];

const productCategories = [
  { to: '/produkty', label: 'Wszystkie produkty' },
  { to: '/produkty?category=zelki', label: 'Żelki THC' },
  { to: '/produkty?category=syropy', label: 'Syropy' },
  { to: '/produkty?category=herbaty', label: 'Herbaty' },
  { to: '/produkty?category=shoty', label: 'Shoty' },
  { to: '/produkty?category=czekoladki', label: 'Ciastka i czekoladki' },
  { to: '/produkty?category=tabletki', label: 'Tabletki' },
  { to: '/produkty?category=conez', label: 'Cali Conez' },
];

const Navbar = () => {
  const { count } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/85 backdrop-blur-md border-b border-white/10' : 'bg-gradient-to-b from-black/80 to-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-[74px] gap-4">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src="/products/site-logo.png" alt="Cali Boyz" className="h-11 sm:h-12 w-auto max-w-[150px] sm:max-w-[180px] object-contain" />
            </Link>

            <nav className="hidden lg:flex items-center gap-6">
              <NavLink
                to="/"
                className={({ isActive }) => `text-[13px] font-semibold tracking-wide transition-colors ${isActive ? 'text-[#a6e22e]' : 'text-white/80 hover:text-[#a6e22e]'}`}
              >
                STRONA GŁÓWNA
              </NavLink>

              <div className="relative group">
                <NavLink
                  to="/produkty"
                  className={({ isActive }) => `inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-wide transition-colors ${isActive ? 'text-[#a6e22e]' : 'text-white/80 hover:text-[#a6e22e]'}`}
                >
                  PRODUKTY <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="invisible absolute left-1/2 top-full z-50 mt-4 w-60 -translate-x-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:mt-3 group-hover:opacity-100">
                  <div className="absolute -top-3 left-0 right-0 h-3" />
                  <div className="rounded-lg border border-white/10 bg-black/95 p-2 shadow-2xl shadow-black/60 backdrop-blur-md">
                    {productCategories.map((category) => (
                      <Link
                        key={category.to}
                        to={category.to}
                        className="block rounded-md px-3 py-2.5 text-sm font-semibold text-white/75 transition hover:bg-white/5 hover:text-[#a6e22e]"
                      >
                        {category.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) => `text-[13px] font-semibold tracking-wide transition-colors ${isActive ? 'text-[#a6e22e]' : 'text-white/80 hover:text-[#a6e22e]'}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center justify-center h-8 px-2.5 rounded-full border border-[#ff2e2e]/60 text-[#ff5a5a] text-xs font-bold">18+</span>
              <button aria-label="Konto" className="hidden sm:grid place-items-center h-9 w-9 rounded-full border border-white/15 text-white/80 hover:text-[#a6e22e] hover:border-[#a6e22e]/50 transition">
                <User size={17} />
              </button>
              <button aria-label="Koszyk" onClick={() => setCartOpen(true)} className="relative grid place-items-center h-9 w-9 rounded-full border border-white/15 text-white/80 hover:text-[#a6e22e] hover:border-[#a6e22e]/50 transition">
                <ShoppingCart size={17} />
                {count > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 grid place-items-center rounded-full bg-[#ff2e2e] text-white text-[11px] font-bold">{count}</span>
                )}
              </button>
              <Link to="/produkty" className="hidden sm:inline-flex btn-green px-5 py-2 text-sm">SKLEP</Link>
              <button className="lg:hidden grid place-items-center h-9 w-9 text-white" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-black/95 border-t border-white/10 px-4 py-4 cb-fade-up">
            <nav className="flex flex-col gap-1">
              <NavLink to="/" className={({ isActive }) => `py-2.5 px-2 rounded-lg text-sm font-semibold ${isActive ? 'text-[#a6e22e] bg-white/5' : 'text-white/80'}`}>
                STRONA GŁÓWNA
              </NavLink>
              <NavLink to="/produkty" className={({ isActive }) => `py-2.5 px-2 rounded-lg text-sm font-semibold ${isActive ? 'text-[#a6e22e] bg-white/5' : 'text-white/80'}`}>
                PRODUKTY
              </NavLink>
              <div className="grid grid-cols-2 gap-2 px-2 pb-2">
                {productCategories.slice(1).map((category) => (
                  <Link
                    key={category.to}
                    to={category.to}
                    className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/70"
                  >
                    {category.label}
                  </Link>
                ))}
              </div>
              {navLinks.map((l) => (
                <NavLink key={l.to} to={l.to} className={({ isActive }) => `py-2.5 px-2 rounded-lg text-sm font-semibold ${isActive ? 'text-[#a6e22e] bg-white/5' : 'text-white/80'}`}>
                  {l.label}
                </NavLink>
              ))}
              <Link to="/produkty" className="btn-green text-center py-3 mt-2">SKLEP</Link>
            </nav>
          </div>
        )}
      </header>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

export default Navbar;
